import { forwardRef, useCallback, useEffect, useId, useMemo, useRef } from 'react';
import { Box, Typography } from '@mui/material';

import type ReactQuill from 'react-quill-new';

import BaseFieldContainer from '../common/baseFieldContainer';
import { hasRenderableHelperText, mergeSx } from '../utils/general';
import QuillToolbar from './components/QuillToolbar';
import {
  DEFAULT_QUILL_EDITOR_MIN_HEIGHT,
  EMPTY_QUILL_HTML,
  QUILL_FORMATS,
} from './constants';
import { buildQuillEditorSx, buildQuillHelperTextSx, buildQuillLabelSx } from './styles';

import type {
  FQuillEditorBaseProps,
  QuillEditorInstance,
  QuillSelection,
  QuillToolbarValue,
} from './types';

const FQuillEditorBase = forwardRef<HTMLDivElement, FQuillEditorBaseProps>(function FQuillEditorBase(
  {
    value = '',
    onChange,
    onBlur,
    onToolbarSuggest,
    toolbarSuggestLabel = 'Suggest narrative',
    toolbarSuggestDisabled = false,
    onToolbarTemplate,
    toolbarTemplateLabel = 'Apply template',
    toolbarTemplateDisabled = false,
    label,
    placeholder,
    helperText,
    readOnly = false,
    disabled = false,
    required,
    error = false,
    fullWidth = true,
    minHeight = DEFAULT_QUILL_EDITOR_MIN_HEIGHT,
    sx,
    editorComponent: Editor,
  },
  ref,
) {
  const quillRef = useRef<ReactQuill | null>(null);
  const lastSelectionRef = useRef<QuillSelection | null>(null);
  const lastValueRef = useRef(value);
  const toolbarId = `f-quill-toolbar-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const toolbarDisabled = readOnly || disabled;

  useEffect(() => {
    lastValueRef.current = value;
  }, [value]);

  const normalizeSelection = useCallback(
    (quill: QuillEditorInstance, selection: QuillSelection): QuillSelection => {
      const maxIndex = Math.max(quill.getLength() - 1, 0);
      const index = Math.min(selection.index, maxIndex);

      return {
        index,
        length: Math.min(selection.length, Math.max(quill.getLength() - index, 0)),
      };
    },
    [],
  );

  const getActiveSelection = useCallback(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return null;

    const currentSelection = quill.getSelection();
    if (currentSelection) {
      lastSelectionRef.current = currentSelection;
      return { quill, selection: currentSelection };
    }

    if (lastSelectionRef.current) {
      const selection = normalizeSelection(quill, lastSelectionRef.current);
      quill.setSelection(selection, 'silent');
      lastSelectionRef.current = selection;
      return { quill, selection };
    }

    const focusedSelection = quill.getSelection(true);
    if (focusedSelection) {
      lastSelectionRef.current = focusedSelection;
      return { quill, selection: focusedSelection };
    }

    const selection: QuillSelection = { index: Math.max(quill.getLength() - 1, 0), length: 0 };
    quill.setSelection(selection, 'silent');
    lastSelectionRef.current = selection;

    return { quill, selection };
  }, [normalizeSelection]);

  const handleInlineFormat = useCallback((formatName: string, value: QuillToolbarValue) => {
    const activeSelection = getActiveSelection();
    if (!activeSelection) return;

    activeSelection.quill.format(formatName, Boolean(value), 'user');
  }, [getActiveSelection]);

  const handleColorFormat = useCallback((value: string | false) => {
    const activeSelection = getActiveSelection();
    if (!activeSelection) return;

    activeSelection.quill.format('color', value || false, 'user');
  }, [getActiveSelection]);

  const handleListFormat = useCallback((value: string | false) => {
    const activeSelection = getActiveSelection();
    if (!activeSelection || (value !== false && value !== 'bullet' && value !== 'ordered')) return;

    activeSelection.quill.format('list', value, 'user');
  }, [getActiveSelection]);

  const handleChange = useCallback(
    (html: string) => {
      const nextValue = html === EMPTY_QUILL_HTML ? '' : html;
      lastValueRef.current = nextValue;
      onChange?.(nextValue);
    },
    [onChange],
  );

  const handleBlur = useCallback((previousSelection: QuillSelection | null) => {
    if (previousSelection) {
      lastSelectionRef.current = previousSelection;
    }
    onBlur?.(lastValueRef.current);
  }, [onBlur]);

  const handleSelectionChange = useCallback((selection: QuillSelection | null) => {
    if (selection) {
      lastSelectionRef.current = selection;
    }
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: `#${toolbarId}`,
        handlers: {
          bold: (value: QuillToolbarValue) => handleInlineFormat('bold', value),
          italic: (value: QuillToolbarValue) => handleInlineFormat('italic', value),
          underline: (value: QuillToolbarValue) => handleInlineFormat('underline', value),
          color: handleColorFormat,
          list: handleListFormat,
        },
      },
    }),
    [handleColorFormat, handleInlineFormat, handleListFormat, toolbarId],
  );

  return (
    <BaseFieldContainer
      required={required}
      disabled={disabled}
      helperText={helperText}
      error={error}
      fullWidth={fullWidth}
    >
      <Box
        ref={ref}
        sx={mergeSx(buildQuillEditorSx({ error, disabled, minHeight }), sx)}
      >
        <QuillToolbar
          id={toolbarId}
          disabled={toolbarDisabled}
          onSuggest={onToolbarSuggest}
          suggestDisabled={toolbarSuggestDisabled}
          suggestLabel={toolbarSuggestLabel}
          onTemplate={onToolbarTemplate}
          templateDisabled={toolbarTemplateDisabled}
          templateLabel={toolbarTemplateLabel}
        />
        {label ? <Typography sx={buildQuillLabelSx(error)}>{label}</Typography> : null}
        <Editor
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          onChangeSelection={handleSelectionChange}
          onFocus={handleSelectionChange}
          placeholder={placeholder}
          readOnly={toolbarDisabled}
          modules={modules}
          formats={QUILL_FORMATS}
        />
        {hasRenderableHelperText(helperText) ? (
          <Box sx={buildQuillHelperTextSx(error)}>{helperText}</Box>
        ) : null}
      </Box>
    </BaseFieldContainer>
  );
});

export default FQuillEditorBase;
